import { Injectable } from '@angular/core';
import moment from 'moment-timezone';

export interface CustomDateApplyResult {
  fromDate: Date;
  toDate: Date;
}

export interface CustomDateModalOptions {
  fromDate?: Date | null;
  toDate?: Date | null;
  onApply?: (result: CustomDateApplyResult) => void;
  onCancel?: () => void;
}

/** Standard overview APIs: type or fromDate/toDate */
export function overviewDatePayload(
  date: string,
  fromDate: Date | null,
  toDate: Date | null,
): { type?: string; fromDate?: Date; toDate?: Date } {
  if (date === 'custom date' && fromDate && toDate) {
    return { fromDate, toDate };
  }
  return { type: date };
}

/** Rewards overview API: type or startDate/endDate (YYYY-MM-DD) */
export function rewardsDatePayload(
  date: string,
  fromDate: Date | null,
  toDate: Date | null,
): { type?: string; startDate?: string; endDate?: string } {
  if (date === 'custom date' && fromDate && toDate) {
    return {
      startDate: moment(fromDate).format('YYYY-MM-DD'),
      endDate: moment(toDate).format('YYYY-MM-DD'),
    };
  }
  return { type: date };
}

/** Compact range label for filter chips (mobile-friendly). */
export function formatOverviewDateRangeLabel(
  fromDate: Date | null,
  toDate: Date | null,
  compact = false,
): string {
  if (!fromDate || !toDate || !moment(fromDate).isValid() || !moment(toDate).isValid()) {
    return '';
  }
  if (compact) {
    return `${moment(fromDate).format('D MMM')} – ${moment(toDate).format('D MMM YY')}`;
  }
  return `${moment(fromDate).format('DD MMM')} – ${moment(toDate).format('DD MMM YYYY')}`;
}

export const OVERVIEW_DATE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last 7 days', label: 'Last 7 days' },
  { value: 'current week', label: 'Current week' },
  { value: 'last week', label: 'Last week' },
  { value: 'current month', label: 'Current month' },
  { value: 'last month', label: 'Last month' },
  { value: 'current year', label: 'Current Year' },
  { value: 'custom date', label: 'Custom Date' },
] as const;

@Injectable({ providedIn: 'root' })
export class CustomDateModalService {
  visible = false;
  formDate: Date | null = null;
  toDate: Date | null = null;
  dateError = false;
  dateLoading = false;
  mobileActiveTab: 'start' | 'end' = 'start';
  readonly moment = moment;

  private onApplyCallback?: (result: CustomDateApplyResult) => void;
  private onCancelCallback?: () => void;

  open(options?: CustomDateModalOptions): void {
    this.formDate = options?.fromDate ?? null;
    this.toDate = options?.toDate ?? null;
    this.dateError = false;
    this.dateLoading = false;
    this.mobileActiveTab = 'start';
    this.onApplyCallback = options?.onApply;
    this.onCancelCallback = options?.onCancel;
    this.visible = true;
  }

  setMobileTab(tab: 'start' | 'end'): void {
    this.mobileActiveTab = tab;
  }

  onStartDateChange(value: Date | null): void {
    this.formDate = value;
    if (value) {
      this.mobileActiveTab = 'end';
      this.dateError = false;
    }
  }

  onEndDateChange(value: Date | null): void {
    this.toDate = value;
    if (value) {
      this.dateError = false;
    }
  }

  formatDisplayDate(value: Date | null): string {
    if (!this.hasDate(value)) {
      return 'Not selected yet';
    }
    return this.moment(value).format('DD MMM YYYY');
  }

  hasDate(value: Date | null): boolean {
    return value != null && this.moment(value).isValid();
  }

  close(): void {
    this.visible = false;
    this.dateError = false;
    this.dateLoading = false;
  }

  cancel(): void {
    this.onCancelCallback?.();
    this.close();
  }

  submit(): void {
    if (this.formDate && this.toDate) {
      this.dateError = false;
      this.onApplyCallback?.({
        fromDate: this.formDate,
        toDate: this.toDate,
      });
      return;
    }
    this.dateError = true;
    this.dateLoading = false;
    this.mobileActiveTab = !this.formDate ? 'start' : 'end';
  }

  setLoading(loading: boolean): void {
    this.dateLoading = loading;
  }
}
